/**
 * Determines the API base URL based on the current domain.
 * - If domain is thatpixel.art (or www.thatpixel.art), uses https://api.thatpixel.art
 * - Otherwise, uses http://localhost:8080 for development
 */
const getApiBaseUrl = () => {
  // Check if we're in a browser environment
  if (typeof window !== "undefined" && window.location) {
    const hostname = window.location.hostname;
    
    // Check for production domain (with or without www)
    if (hostname === "thatpixel.art" || hostname === "www.thatpixel.art") {
      return "https://api.thatpixel.art";
    }
  }
  
  // Fallback to localhost for development
  return "http://localhost:8080";
};

// Try Vite, CRA/Next, then window override, then domain-based detection
const viteBase =
  typeof import.meta !== "undefined" &&
  import.meta.env &&
  import.meta.env.VITE_API_BASE;

const craNextBase =
  typeof process !== "undefined" &&
  process.env &&
  (process.env.REACT_APP_API_BASE || process.env.NEXT_PUBLIC_API_BASE);

const windowBase =
  typeof window !== "undefined" && (window.__API_BASE__ || window.API_BASE);

const domainBasedBase = getApiBaseUrl();

export const API_BASE = viteBase || craNextBase || windowBase || domainBasedBase;

export const apiUrl = (path = "") => {
  if (!path) return API_BASE;
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
};

/**
 * Normalizes artwork path to ensure consistent format
 * Keeps the path structure as returned from API (files are still stored with username)
 * Handles cases where artwork_path might already include /artwork/ prefix
 * @param {string} path - The artwork path (e.g., "/artwork/Dario/filename.png" or "Dario/filename.png" or "filename.png")
 * @returns {string} - Normalized path starting with /artwork/ (preserving username if present)
 */
export const normalizeArtworkPath = (path) => {
  if (!path) return '/artwork/';
  let normalized = path;
  
  // Remove /artwork/ prefix if present to avoid duplication
  if (normalized.startsWith('/artwork/')) {
    normalized = normalized.substring('/artwork/'.length);
  }
  
  // Remove leading slash if present
  if (normalized.startsWith('/')) {
    normalized = normalized.substring(1);
  }
  
  // Return with /artwork/ prefix, keeping the original structure
  // Backend returns paths like "/artwork/username/filename.png" and files are stored that way
  // So we preserve the structure as-is
  return `/artwork/${normalized}`;
};

/**
 * Fetches an image with authentication headers and returns an object URL
 * This is useful for images that require authentication (like artwork images in admin pages)
 * @param {string} imagePath - The path to the image (e.g., "/artwork/image.jpg")
 * @returns {Promise<string>} - A promise that resolves to an object URL
 */
export const fetchAuthenticatedImage = async (imagePath) => {
  const url = apiUrl(imagePath);
  const response = await fetch(url, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
};